import type { ApiGnomeSnapshot } from "@bridge-applications/pb-types";
import { useSlice } from "@bridge-applications/provider-kit";
import { useCallback, useLayoutEffect, useMemo, useRef } from "react";
import { CAREER_CONTENT_SOURCE } from "../helpers/careerContentSource";
import { gnomeSnapshotReader } from "../helpers/gnomeSnapshotRequest";
import {
    acceptGnomeSnapshot,
    ownsGnomeSnapshot,
} from "../helpers/gnomeSnapshotState";
import {
    EMPTY_GNOME_EQUIPMENT,
    equippedItemsFromSnapshot,
} from "../helpers/gnomeSnapshotAppearance";
import { isCurrentGnomeSnapshotVersion } from "../helpers/gnomeCustomizationSync";
import { acknowledgeQuestEquipmentChange } from "../helpers/careerEquipmentRewards";
import type { GnomeSnapshotReadIntent } from "../helpers/gnomeSnapshotReader";

export function useGnomeSnapshot() {
    const { state: auth } = useSlice("authSlice");
    const { state, setSlice } = useSlice("gnomeCustomizationSlice");
    const { setSlice: setPresentation } = useSlice(
        "gnomeCareerPresentationSlice",
    );
    const authRef = useRef(auth);
    const stateRef = useRef(state);
    authRef.current = auth;
    stateRef.current = state;
    const owner = useMemo(
        () =>
            auth.bootstrapStatus === "ready" && auth.userId
                ? { userId: auth.userId, generation: auth.sessionGeneration }
                : null,
        [auth.bootstrapStatus, auth.sessionGeneration, auth.userId],
    );
    const latestSnapshotRef = useRef<ApiGnomeSnapshot | null>(null);
    const isCurrentSession = useCallback(
        () =>
            CAREER_CONTENT_SOURCE === "local" ||
            (owner !== null &&
                gnomeSnapshotReader.isCurrentOwner(owner) &&
                authRef.current.bootstrapStatus === "ready" &&
                authRef.current.userId === owner.userId &&
                authRef.current.sessionGeneration === owner.generation),
        [owner],
    );

    useLayoutEffect(() => {
        if (CAREER_CONTENT_SOURCE === "local") return;
        gnomeSnapshotReader.cancelOtherOwners(owner);
        setSlice((previous) => {
            if (owner && ownsGnomeSnapshot(previous, owner)) return {};
            if (!owner && previous.snapshotUserId === null) return {};
            return {
                snapshotUserId: owner?.userId ?? null,
                snapshotGeneration: owner?.generation ?? 0,
                snapshot: null,
                snapshotFetchedAt: null,
                backgroundReadStatus: "idle",
                pendingMutationCount: 0,
                syncStatus: "client-only",
                syncError: null,
                refreshRequestedRevision: 0,
                backendEquipmentReward: null,
                displayName: null,
                skinColorId: "source",
                facialExpression: "expressions/dopey_neutral",
                equippedItemIds: { ...EMPTY_GNOME_EQUIPMENT },
            };
        });
        latestSnapshotRef.current = null;
    }, [owner, setSlice]);

    if (
        owner &&
        ownsGnomeSnapshot(state, owner) &&
        state.snapshot &&
        isCurrentGnomeSnapshotVersion(
            state.snapshot.character.version,
            latestSnapshotRef.current?.character.version,
        )
    ) {
        latestSnapshotRef.current = state.snapshot;
    }

    const applySnapshot = useCallback(
        (
            snapshot: ApiGnomeSnapshot,
            completingMutation = false,
            quiet = false,
            source: "background" | "foreground" = "foreground",
        ) => {
            if (!owner || !isCurrentSession()) return;
            const before = latestSnapshotRef.current;
            const incoming = isCurrentGnomeSnapshotVersion(
                snapshot.character.version,
                before?.character.version,
            )
                ? snapshot
                : (before ?? snapshot);
            if (source === "foreground") latestSnapshotRef.current = incoming;
            if (source === "foreground" && quiet && before !== null) {
                setPresentation((previous) => {
                    if (!isCurrentSession()) return {};
                    return {
                        observedAppearance: {
                            skinColorId:
                                previous.observedAppearance?.skinColorId ??
                                before.character.baseCustomization.skinColorId,
                            equipment: acknowledgeQuestEquipmentChange(
                                previous.observedAppearance?.equipment ??
                                    equippedItemsFromSnapshot(before),
                                equippedItemsFromSnapshot(before),
                                equippedItemsFromSnapshot(incoming),
                            ),
                        },
                    };
                });
            }
            setSlice((previous) =>
                isCurrentSession()
                    ? acceptGnomeSnapshot(
                          previous,
                          incoming,
                          owner,
                          source,
                          completingMutation,
                          Date.now(),
                      )
                    : {},
            );
        },
        [isCurrentSession, owner, setPresentation, setSlice],
    );

    const requestSnapshot = useCallback(
        async (
            intent: GnomeSnapshotReadIntent,
            fresh = false,
        ): Promise<ApiGnomeSnapshot | null> => {
            if (
                !owner ||
                !isCurrentSession() ||
                CAREER_CONTENT_SOURCE !== "backend"
            )
                return null;
            const cached = stateRef.current;
            if (
                !fresh &&
                ownsGnomeSnapshot(cached, owner) &&
                cached.snapshot &&
                cached.snapshotFetchedAt !== null &&
                Date.now() - cached.snapshotFetchedAt < 30_000
            )
                return cached.snapshot;
            if (intent === "existing")
                setSlice((previous) =>
                    ownsGnomeSnapshot(previous, owner)
                        ? { backgroundReadStatus: "loading" }
                        : {},
                );
            try {
                const snapshot = await gnomeSnapshotReader.read(
                    owner,
                    intent,
                    fresh,
                );
                if (!isCurrentSession()) return null;
                if (intent === "existing") {
                    if (snapshot)
                        applySnapshot(snapshot, false, false, "background");
                    else
                        setSlice((previous) =>
                            ownsGnomeSnapshot(previous, owner)
                                ? { backgroundReadStatus: "absent" }
                                : {},
                        );
                }
                return snapshot;
            } catch (error: unknown) {
                if (intent === "existing" && isCurrentSession())
                    setSlice((previous) =>
                        ownsGnomeSnapshot(previous, owner)
                            ? { backgroundReadStatus: "failed" }
                            : {},
                    );
                throw error;
            }
        },
        [applySnapshot, isCurrentSession, owner, setSlice],
    );

    return {
        state,
        setSlice,
        owner,
        latestSnapshotRef,
        isCurrentSession,
        applySnapshot,
        requestSnapshot,
    };
}
